/**
 * Brand Product Detail Component
 * Read-only view of product details for brand portal
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Heart, Bookmark, ExternalLink, Instagram, Globe, Edit, Trash2, Tag, Package } from 'lucide-react';
import { useBrand } from '../../context/BrandContext';
import ImageWithFallback from './ImageWithFallback';
import { formatPrice } from '../../services/currencyService';

export function BrandProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { authFetch, isAuthenticated } = useBrand();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/brand-portal/login');
      return;
    }

    const fetchProduct = async () => {
      try {
        const response = await authFetch(`/api/brand/products/${productId}/`);
        const data = await response.json();

        if (response.ok) {
          setProduct(data);
        } else {
          setError(data.error || 'Product not found');
        }
      } catch (err) {
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, authFetch, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <span style={{ color: '#71717a' }}>Loading product...</span>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorContent}>
          <h3 style={styles.errorTitle}>Product Not Found</h3>
          <p style={styles.errorMessage}>{error || 'This product could not be found or you don\'t have permission to view it.'}</p>
          <Link to="/brand-portal/collection" style={styles.backButton}>
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back to Collection
          </Link>
        </div>
      </div>
    );
  }

  const formatPriceDisplay = () => {
    return formatPrice(product.price, product.currency);
  };

  const getLinkIcon = (linkType) => {
    return linkType === 'instagram' ? Instagram : Globe;
  };

  const getLinkLabel = (linkType) => {
    return linkType === 'instagram' ? 'Instagram' : 'Website';
  };

  return (
    <div>
      {/* Header */}
      <div className="bp-header-row" style={styles.header}>
        <Link to="/brand-portal/collection" style={styles.backLink}>
          <ArrowLeft style={{ width: '20px', height: '20px' }} />
          Back to Collection
        </Link>
        
        <div style={styles.headerActions}>
          <Link 
            to={`/brand-portal/products/${product.id}/edit`} 
            style={styles.editButton}
          >
            <Edit style={{ width: '16px', height: '16px' }} />
            Edit Product
          </Link>
        </div>
      </div>

      {/* Product Content */}
      <div style={styles.content}>
        <div className="bp-product-layout" style={styles.productLayout}>
          {/* Product Images */}
          <div style={styles.imageSection}>
            {(() => {
              const allImages = [product.image, ...(product.additional_images || [])].filter(Boolean);
              const selectedImage = allImages[selectedImageIndex] || product.image;
              return (
                <div style={{ display: 'flex', gap: '10px' }}>
                  {/* Thumbnail Strip */}
                  {allImages.length > 1 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      flexShrink: 0,
                    }}>
                      {allImages.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedImageIndex(index)}
                          style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: selectedImageIndex === index
                              ? '2px solid #171717'
                              : '2px solid transparent',
                            padding: 0,
                            cursor: 'pointer',
                            background: '#f5f5f5',
                            opacity: selectedImageIndex === index ? 1 : 0.6,
                            transition: 'all 0.2s',
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={(e) => {
                            if (selectedImageIndex !== index) e.currentTarget.style.opacity = '0.6';
                          }}
                        >
                          <img
                            src={img}
                            alt={`${product.title} ${index + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/default-image.avif'; }}
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Main Image */}
                  <div style={{ ...styles.productImage, flex: 1, minWidth: 0 }}>
                    <ImageWithFallback 
                      src={selectedImage}
                      alt={product.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                </div>
              );
            })()}
            
            {/* Stock Badge */}
            <div style={styles.stockBadge}>
              {product.is_manual ? 'Manual Product' : 'In Stock'}
            </div>
          </div>

          {/* Product Info */}
          <div style={styles.infoSection}>
            <div style={styles.productHeader}>
              <h1 style={styles.productTitle}>{product.title}</h1>
              <div style={styles.productMeta}>
                {product.sku && (
                  <span style={styles.sku}>SKU: {product.sku}</span>
                )}
                <span style={styles.price}>{formatPriceDisplay()}</span>
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Description</h3>
                <p style={styles.description}>{product.description}</p>
              </div>
            )}

            {/* Categories */}
            {product.categories && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  <Tag style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Categories
                </h3>
                <div style={styles.categories}>
                  {(() => {
                    let categoriesArray = [];
                    if (Array.isArray(product.categories)) {
                      categoriesArray = product.categories;
                    } else if (typeof product.categories === 'string') {
                      categoriesArray = product.categories.split(',').map(cat => cat.trim());
                    }
                    return categoriesArray.map((category, index) => (
                      <span key={index} style={styles.category}>
                        {category}
                      </span>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Product Link */}
            {product.product_link && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  <ExternalLink style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Product Link
                </h3>
                <a 
                  href={product.product_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={styles.productLink}
                >
                  {(() => {
                    const Icon = getLinkIcon(product.link_type);
                    return <Icon style={{ width: '16px', height: '16px', marginRight: '8px' }} />;
                  })()}
                  View on {getLinkLabel(product.link_type)}
                </a>
              </div>
            )}

            {/* Performance Stats */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Performance</h3>
              <div style={styles.stats}>
                <div style={styles.stat}>
                  <Eye style={{ width: '16px', height: '16px', color: '#71717a' }} />
                  <div>
                    <div style={styles.statValue}>
                      {product.views_count >= 1000 
                        ? `${(product.views_count / 1000).toFixed(1)}K` 
                        : product.views_count || 0}
                    </div>
                    <div style={styles.statLabel}>Views</div>
                  </div>
                </div>
                
                {product.saves_count > 0 && (
                  <div style={styles.stat}>
                    <Heart style={{ width: '16px', height: '16px', color: '#ef4444' }} />
                    <div>
                      <div style={styles.statValue}>{product.saves_count}</div>
                      <div style={styles.statLabel}>Saves</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Details */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <Package style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                Product Details
              </h3>
              <div className="bp-details-grid" style={styles.detailsGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Product ID:</span>
                  <span style={styles.detailValue}>#{product.id}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Currency:</span>
                  <span style={styles.detailValue}>{product.currency}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Type:</span>
                  <span style={styles.detailValue}>{product.is_manual ? 'Manual' : 'Scraped'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Created:</span>
                  <span style={styles.detailValue}>
                    {new Date(product.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  errorContent: {
    textAlign: 'center',
    maxWidth: '400px',
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#171717',
    marginBottom: '8px',
  },
  errorMessage: {
    fontSize: '14px',
    color: '#71717a',
    marginBottom: '24px',
    lineHeight: 1.5,
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#171717',
    color: '#ffffff',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#71717a',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'color 0.2s',
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#ffffff',
    border: '1px solid #171717',
    borderRadius: '8px',
    color: '#171717',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  content: {
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e5e5',
    overflow: 'hidden',
    padding: '32px',
  },
  productLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '48px',
  },
  imageSection: {
    position: 'relative',
  },
  productImage: {
    aspectRatio: '1',
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#f5f5f5',
  },
  stockBadge: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.95)',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#171717',
  },
  infoSection: {
    padding: '20px 0',
  },
  productHeader: {
    marginBottom: '32px',
  },
  productTitle: {
    fontSize: '32px',
    fontWeight: '400',
    color: '#171717',
    margin: '0 0 16px 0',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  productMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  sku: {
    fontSize: '14px',
    color: '#71717a',
    fontFamily: 'monospace',
  },
  price: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#171717',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#171717',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  description: {
    fontSize: '15px',
    lineHeight: '1.6',
    color: '#52525b',
    margin: 0,
  },
  categories: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  category: {
    padding: '6px 12px',
    background: '#f5f5f5',
    borderRadius: '16px',
    fontSize: '13px',
    color: '#52525b',
  },
  productLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: '#171717',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'color 0.2s',
  },
  stats: {
    display: 'flex',
    gap: '32px',
  },
  stat: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#171717',
  },
  statLabel: {
    fontSize: '12px',
    color: '#71717a',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #f5f5f5',
  },
  detailLabel: {
    fontSize: '14px',
    color: '#71717a',
  },
  detailValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
  },
};

export default BrandProductDetail;
